import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import './FAQ.css'

const FAQS = [
  {
    section: 'About HumanOrigin',
    items: [
      { q: 'What is HumanOrigin?', a: 'HumanOrigin is a verification platform that confirms creative work was made by a real human being. We review your submission and evidence, and if verified, issue a permanent certificate with a QR code proving human origin.' },
      { q: 'Why does this exist?', a: 'AI-generated content is flooding the internet. Artists, writers, musicians, and other creators deserve a way to prove their work is genuinely human-made — for buyers, clients, galleries, publishers, and the world at large.' },
      { q: 'Who reviews submissions?', a: 'Other verified creators in the community review each submission and vote on whether the evidence trail looks genuinely human-made. It takes agreement from several independent reviewers, not just one person, before a project is verified. A small admin team exists as a backstop for disputes.' },
    ]
  },
  {
    section: 'Submitting work',
    items: [
      { q: 'What types of work can I submit?', a: 'Virtually any creative work — writing, novels, poetry, visual art, photography, music, voiceovers, podcasts, video editing, animation, code, architecture, handcraft, sculpture, ceramics, game design, and more. If you made it, we can verify it.' },
      { q: 'What counts as good evidence?', a: 'Process videos or timelapses (e.g. on YouTube), raw project files shared via Google Drive or Dropbox, work-in-progress photos, handwritten drafts, Git commit history, DAW session files (Ableton, FL Studio), Procreate timelapses, or any material that demonstrates your creative process over time.' },
      { q: 'Why do I need to provide links instead of uploading files?', a: 'To keep the platform accessible and fast, we ask for evidence via links (YouTube, Google Drive, Dropbox, GitHub, etc.) rather than direct uploads. This also means your evidence can be as large as you need.' },
      { q: 'Can I submit physical art?', a: 'Yes. For physical artwork like paintings, sculpture, ceramics, or handmade crafts, provide photos or video of the creation process, your workspace, raw materials, and work-in-progress stages.' },
      { q: 'What if my evidence is rejected?', a: 'You\'ll see reviewer notes explaining what additional evidence is needed. You can add more and resubmit at no cost — everything here is free.' },
    ]
  },
  {
    section: 'Free & community reviewed',
    items: [
      { q: 'How much does this cost?', a: 'Nothing. Submitting, verifying, and holding a certificate are all free, forever — no credit card, no subscription, no limits on how many projects you can start.' },
      { q: 'Why is it free now?', a: 'HumanOrigin used to be a paid service with admin review. We\'ve moved to community review instead, which means verification isn\'t capped by how many submissions one small team can get through — so there\'s no need to charge for it.' },
      { q: 'Do I have to review other people\'s work?', a: 'To submit your own project for verification, you\'ll need to review 2 other projects first. This keeps the review queue moving and means the community verifies itself rather than waiting on a paid team.' },
      { q: 'How does a project actually get verified?', a: 'Once you submit, independent reviewers look at your full commit trail — sketches, drafts, process footage, whatever you\'ve logged — and each casts a verdict. A project is verified once enough reviewers agree it looks genuine.' },
      { q: 'What stops people from gaming the reviews?', a: 'Reviewer agreement alone doesn\'t automatically verify anything from just one clique — and both flags and verdicts factor in the reviewer\'s own track record over time.' },
    ]
  },
  {
    section: 'Certificates',
    items: [
      { q: 'What is a HumanOrigin certificate?', a: 'A permanent, publicly accessible webpage for each verified work. It includes the creator\'s name, the work\'s title, verification status, how many independent community reviewers verified it, a unique ID, and a QR code. Think of it like a diploma — but for your creative work.' },
      { q: 'Can I download my certificate?', a: 'Yes. Use the "Download Certificate" button on the certificate page to save it as a PNG image. You can also download just the QR code separately using the "Download QR" button — useful for embedding in portfolios, social profiles, or printed materials.' },
      { q: 'How do I share my certificate?', a: 'Use the "Share" button to share the certificate URL on any platform. Each certificate has a unique, permanent link and QR code you can embed anywhere.' },
      { q: 'Can I show my evidence on the certificate?', a: 'Yes. Toggle the "Show evidence publicly" option on your certificate page. Viewers will be able to see the evidence links you submitted, giving full transparency.' },
      { q: 'Can viewers contact me through the certificate?', a: 'Optionally. If you add contact information to your profile and enable it, your contact details will appear on your certificate page.' },
      { q: 'What if I want my work to be private?', a: 'You can set a submission to "Unlisted" during submission. Unlisted works won\'t appear in browse or on your public profile, but are still accessible via direct link for anyone who has the URL.' },
    ]
  },
  {
    section: 'Accounts & profiles',
    items: [
      { q: 'Can I use a display name instead of my real name?', a: 'Yes. Set a display name in your profile settings. This will appear on your certificate instead of your legal name.' },
      { q: 'How do I become a reviewer?', a: 'Everyone is a reviewer. Head to the review queue and start reviewing — it\'s also required before you can submit your own project.' },
      { q: 'How do I become an admin?', a: 'Admin access is granted manually to a small trust-and-safety team who step in on disputes. It\'s separate from reviewing, which is open to everyone.' },
    ]
  },
]

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`faq-accordion ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-q">
        <span>{q}</span>
        <span className="faq-chevron">{open ? '−' : '+'}</span>
      </div>
      {open && <div className="faq-a"><p>{a}</p></div>}
    </div>
  )
}

export default function FAQ() {
  return (
    <div className="faq-page page-fade">
      <div className="container">
        <div className="faq-header">
          <h1>Frequently asked questions</h1>
          <p>Everything you need to know about HumanOrigin.</p>
        </div>
        {FAQS.map(section => (
          <div key={section.section} className="faq-section">
            <h2 className="faq-section-title">{section.section}</h2>
            <div className="faq-items">
              {section.items.map(item => <FAQItem key={item.q} {...item} />)}
            </div>
          </div>
        ))}
        <div className="faq-cta card">
          <h3>Still have questions?</h3>
          <p>Can't find the answer you're looking for? Reach out via your verified profile or submit a work and our team will be in touch.</p>
          <Link to="/login" className="btn btn-primary" style={{ marginTop: 16 }}>Get started →</Link>
        </div>
      </div>
    </div>
  )
}
